import { useCallback, useMemo } from 'react';

/**
 * Hook personalizado para verificar permisos de usuario
 * @returns {Object} Objeto con funciones para verificar permisos
 */
const usePermission = () => {
  // Obtener información del usuario del localStorage
  const userData = useMemo(() => {
    try {
      const data = localStorage.getItem('userData');
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error al obtener datos del usuario:', error);
      return null;
    }
  }, []);

  /**
   * Verifica si el usuario tiene un permiso específico
   * @param {string} permissionName - Nombre del permiso a verificar
   * @param {string} action - Acción a verificar (listView, create, update, delete)
   * @returns {boolean} true si tiene el permiso, false en caso contrario
   */
  const hasPermission = useCallback((permissionName, action = 'listView') => {
    if (!userData || !userData.role || !userData.role.permissions) {
      return false;
    }

    const permission = userData.role.permissions.find(
      perm => perm.permissionName === permissionName
    );

    return permission ? permission[action] === true : false;
  }, [userData]);

  /**
   * Verifica si el usuario tiene al menos uno de los permisos especificados
   * @param {Array<{name: string, action: string}>} permissions - Array de permisos a verificar
   * @returns {boolean} true si tiene al menos uno de los permisos, false en caso contrario
   */
  const hasAnyPermission = useCallback((permissions = []) => {
    return permissions.some(({ name, action = 'listView' }) =>
      hasPermission(name, action)
    );
  }, [hasPermission]);

  /**
   * Verifica si el usuario tiene todos los permisos especificados
   * @param {Array<{name: string, action: string}>} permissions - Array de permisos a verificar
   * @returns {boolean} true si tiene todos los permisos, false en caso contrario
   */
  const hasAllPermissions = useCallback((permissions = []) => {
    return permissions.every(({ name, action = 'listView' }) =>
      hasPermission(name, action)
    );
  }, [hasPermission]);

  /**
   * Obtiene todos los permisos del usuario
   * @returns {Array} Array de objetos de permisos
   */
  const getAllPermissions = useCallback(() => {
    if (!userData || !userData.role || !userData.role.permissions) {
      return [];
    }
    return userData.role.permissions;
  }, [userData]);

  /**
   * Verifica si el usuario es administrador
   * @returns {boolean} true si el usuario tiene rol ADMIN, false en caso contrario
   */
  const isAdmin = useCallback(() => {
    return userData?.role?.roleName === 'ADMIN';
  }, [userData]);

  /**
   * Verifica si el usuario es administrador SaaS de la plataforma
   * @returns {boolean} true si el usuario tiene isSaasAdmin=true, false en caso contrario
   */
  const isSaasAdmin = useCallback(() => {
    return userData?.isSaasAdmin === true;
  }, [userData]);

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getAllPermissions,
    isAdmin,
    isSaasAdmin,
    userData
  };
};

export default usePermission;
